import { DefaultSearchType } from 'algolia-api/types/DefaultSearchType';
import { Dropdown } from 'components/Dropdown/Dropdown';
import { DropdownHeaderTrigger } from 'components/Dropdown/HeaderTrigger/DropdownHeaderTrigger';
import { closeDropdownFromElementWithin } from 'components/Dropdown/closeDropdownFromElementWithin';
import { Icon } from 'components/Icon';
import { AmpPageHeader } from 'components/PageHeader/Amp/AmpPageHeader';
import { PageHeader } from 'components/PageHeader/PageHeader';
import { PageHeaderSearchInputsDesktop } from 'components/PageHeader/Search/Desktop/PageHeaderSearchInputsDesktop';
import { PageHeaderMobileSearchMenu } from 'components/PageHeader/Search/Mobile/PageHeaderMobileSearchMenu';
import { useLogin } from 'hooks/useLogin';
import { useMainSearchMenu } from 'modules/main/store/hooks/useMainSearchMenu';
import { useUserEnvironment } from 'modules/main/store/hooks/useUserEnvironment';
import { useAmpContext } from 'utils/AmpContext';

import { CartPillContainer } from './CartPillContainer';
import { MyAccountDropdownContainer } from './MyAccountDropdownContainer';
import { NotificationsDropdownContainer } from './NotificationsDropdownContainer';

type Props = {
  showDesktopSearch: boolean;
  defaultSearchType: DefaultSearchType | undefined;
  searchMode: boolean;
};

export function PageHeaderContainer({
  showDesktopSearch,
  defaultSearchType,
  searchMode,
}: Props) {
  const userEnvironment = useUserEnvironment();
  const { user, isUserLoaded } = userEnvironment;
  const isAmp = useAmpContext();

  const mainProps = useMainSearchMenu({
    searchFrom: 'Header Search Bar',
    searchMode,
    defaultSearchType,
  });

  const { loginLink, onClickLogin } = useLogin();

  if (isAmp) return <AmpPageHeader />;

  return (
    <PageHeader
      locale={CURRENT_LOCALE}
      loginHref={loginLink}
      isUserLoaded={isUserLoaded}
      onClickLogin={onClickLogin}
      userDisplayName={user?.name || user?.email}
      orgs={user?.orgs || []}
      notificationsDropdown={<NotificationsDropdownContainer />}
      myAccountDropdown={<MyAccountDropdownContainer />}
      cartPill={<CartPillContainer />}
      desktopSearch={
        showDesktopSearch && (
          <PageHeaderSearchInputsDesktop
            selectedType={mainProps.selectedType}
            listingTypes={mainProps.listingTypes}
            search={mainProps.search}
            query={mainProps.query}
            initialQuery={mainProps.initialQuery}
            searchLocation={mainProps.searchLocation}
            setListingType={mainProps.setListingType}
            clearSearchInput={mainProps.clearSearchInput}
            setQuery={mainProps.setQuery}
            onLocationChange={mainProps.onLocationChange}
            searchInputId="search-query"
            searchInputQaId="search-input"
          />
        )
      }
      mobileSearchDropdown={
        <Dropdown
          trigger={
            <DropdownHeaderTrigger
              ariaLabel={getText('Toggle search')}
              qaId="search-menu-button"
              variant="grey"
            >
              <Icon name="search" size={20} />
            </DropdownHeaderTrigger>
          }
        >
          <PageHeaderMobileSearchMenu
            onClose={(event) =>
              closeDropdownFromElementWithin(event.target as HTMLElement)
            }
            onSearch={(event) =>
              closeDropdownFromElementWithin(event.target as HTMLElement)
            }
            selectedType={mainProps.selectedType}
            listingTypes={mainProps.listingTypes}
            search={mainProps.search}
            initialQuery={mainProps.initialQuery}
            query={mainProps.query}
            searchLocation={mainProps.searchLocation || undefined}
            setListingType={mainProps.setListingType}
            clearSearchInput={mainProps.clearSearchInput}
            setUserQuery={mainProps.setQuery}
            onLocationChange={mainProps.onLocationChange}
            showPopularSearches
          />
        </Dropdown>
      }
    />
  );
}
